import type { STPreset, WorldBookEntry } from '@/IsekaiRPG/modules/llm/llmTypes';

const PRIMARY_PROMPT_ORDER_CHARACTER_ID = 100001;

export class STNativeMacroResolver {
  static applyToPreset(preset: STPreset, rng: () => number): STPreset {
    if (!preset.enabled) {
      return preset;
    }

    const activePromptIdentifiers = this.getActivePromptIdentifiers(preset);

    return {
      ...preset,
      prompts: preset.prompts.map(prompt => {
        if (!activePromptIdentifiers.has(prompt.identifier)) {
          return prompt;
        }

        return {
          ...prompt,
          content: this.resolveText(prompt.content, rng),
        };
      }),
    };
  }

  static applyToWorldBookEntries(entries: WorldBookEntry[], rng: () => number): WorldBookEntry[] {
    return entries.map(entry => ({
      ...entry,
      content: this.resolveText(entry.content, rng),
    }));
  }

  private static getActivePromptIdentifiers(preset: STPreset): Set<string> {
    const primaryOrderBlock =
      preset.prompt_order.find(block => block.character_id === PRIMARY_PROMPT_ORDER_CHARACTER_ID) ??
      preset.prompt_order[0];

    const enabledOrderIdentifiers = new Set(
      (primaryOrderBlock?.order ?? []).filter(entry => entry.enabled).map(entry => entry.identifier),
    );

    return new Set(
      preset.prompts.filter(prompt => enabledOrderIdentifiers.has(prompt.identifier)).map(prompt => prompt.identifier),
    );
  }

  private static resolveText(text: string, rng: () => number): string {
    if (!text.includes('{{')) return text;

    let output = '';
    let index = 0;

    while (index < text.length) {
      const tokenStart = text.indexOf('{{', index);
      if (tokenStart === -1) {
        output += text.slice(index);
        return output;
      }

      output += text.slice(index, tokenStart);

      if (text.startsWith('{{//', tokenStart)) {
        const commentEnd = this.findBalancedMacroEnd(text, tokenStart);
        if (commentEnd === -1) {
          output += text.slice(tokenStart);
          return output;
        }

        index = commentEnd + 2;
        continue;
      }

      const tokenEnd = text.indexOf('}}', tokenStart + 2);
      if (tokenEnd === -1) {
        output += text.slice(tokenStart);
        return output;
      }

      const rawToken = text.slice(tokenStart, tokenEnd + 2);
      const tokenBody = text.slice(tokenStart + 2, tokenEnd).trim();

      if (/^trim$/i.test(tokenBody)) {
        output = this.removeTrailingNewlines(output);
        index = this.skipLeadingNewlines(text, tokenEnd + 2);
        continue;
      }

      const randomOptions = this.parseRandomOptions(tokenBody);
      if (randomOptions) {
        output += this.pickRandomOption(randomOptions, rng);
        index = tokenEnd + 2;
        continue;
      }

      output += rawToken;
      index = tokenEnd + 2;
    }

    return output;
  }

  private static findBalancedMacroEnd(text: string, startIndex: number): number {
    let depth = 1;
    let index = startIndex + 2;

    while (index < text.length - 1) {
      const nextOpen = text.indexOf('{{', index);
      const nextClose = text.indexOf('}}', index);

      if (nextClose === -1) {
        return -1;
      }

      if (nextOpen !== -1 && nextOpen < nextClose) {
        depth += 1;
        index = nextOpen + 2;
        continue;
      }

      depth -= 1;
      if (depth === 0) {
        return nextClose;
      }

      index = nextClose + 2;
    }

    return -1;
  }

  private static parseRandomOptions(tokenBody: string): string[] | null {
    const normalized = tokenBody.trim();
    if (!/^random\b/i.test(normalized)) {
      return null;
    }

    const separatorIndex = normalized.indexOf('::');
    if (separatorIndex === -1) {
      return null;
    }

    const macroName = normalized.slice(0, separatorIndex).trim().toLowerCase();
    if (macroName !== 'random') {
      return null;
    }

    const options = normalized
      .slice(separatorIndex + 2)
      .split(/::/g)
      .map(option => option.trim());

    return options.length > 0 ? options : null;
  }

  private static pickRandomOption(options: string[], rng: () => number): string {
    if (options.length === 0) return '';
    const randomValue = Number.isFinite(rng()) ? rng() : 0;
    const normalizedRandomValue = Math.min(Math.max(randomValue, 0), 0.999999999999);
    const selectedIndex = Math.floor(normalizedRandomValue * options.length);
    return options[selectedIndex] ?? options[0] ?? '';
  }

  private static removeTrailingNewlines(text: string): string {
    return text.replace(/(?:\r?\n)+$/g, '');
  }

  private static skipLeadingNewlines(text: string, startIndex: number): number {
    let index = startIndex;

    while (index < text.length) {
      if (text.slice(index, index + 2) === '\r\n') {
        index += 2;
        continue;
      }

      if (text[index] === '\n') {
        index += 1;
        continue;
      }

      break;
    }

    return index;
  }
}
